import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCode, 
  FaServer, 
  FaMobile, 
  FaShieldAlt,
  FaRocket,
  FaUsers,
  FaLightbulb,
  FaDatabase,
  FaCheck
} from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const iconMap = {
    FaCode: FaCode,
    FaServer: FaServer,
    FaMobile: FaMobile,
    FaShieldAlt: FaShieldAlt,
    FaRocket: FaRocket,
    FaUsers: FaUsers,
    FaLightbulb: FaLightbulb,
    FaDatabase: FaDatabase
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await apiService.getServices();
        console.log('Services response:', response.data);
        
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          setServices(response.data);
        } else {
          // Fallback data if API returns empty
          setServices([
            {
              id: 1,
              title: 'Full Stack Development',
              description: 'End-to-end web application development using modern technologies and best practices.',
              icon: 'FaCode',
              features: ['React/Vue.js frontend development', 'Node.js/PHP backend development', 'REST API design and integration'],
              pricing_text: 'Project-based or hourly'
            },
            {
              id: 2,
              title: 'Blockchain Development',
              description: 'Building decentralized applications and smart contracts on various blockchain platforms.',
              icon: 'FaShieldAlt',
              features: ['Smart contract development (Solidity)', 'dApp development', 'DeFi applications'],
              pricing_text: 'Project-based'
            },
            {
              id: 3,
              title: 'Backend Development',
              description: 'Scalable and secure backend services with robust API architecture.',
              icon: 'FaServer',
              features: ['RESTful API development', 'Microservices architecture', 'Authentication & authorization'],
              pricing_text: 'Project-based or hourly'
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        setError('Failed to load services');
        // Set fallback data on error
        setServices([
          {
            id: 1,
            title: 'Full Stack Development',
            description: 'End-to-end web application development.',
            icon: 'FaCode',
            features: ['React/Vue.js', 'Node.js/PHP', 'REST APIs'],
            pricing_text: 'Project-based'
          },
          {
            id: 2,
            title: 'Blockchain Development',
            description: 'Decentralized applications and smart contracts.',
            icon: 'FaShieldAlt',
            features: ['Solidity', 'dApps', 'DeFi'],
            pricing_text: 'Project-based'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg">{error}</p>
          <p className="text-gray-400 mt-2">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Services
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold">
            What I <span className="gradient-text">Offer</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Professional services tailored to your needs, from development to consulting
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || FaCode;
            const features = Array.isArray(service.features) ? service.features : [];
            
            return (
              <motion.div
                key={service.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 bg-opacity-20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <IconComponent className="text-2xl text-primary-400" />
                </div>

                <h3 className="text-xl font-semibold text-white mb-2">
                  {service.title}
                </h3>

                <p className="text-gray-400 text-sm mb-4">
                  {service.description}
                </p>

                {features.length > 0 && (
                  <ul className="space-y-2 mb-4">
                    {features.slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <FaCheck className="text-primary-400 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {service.pricing_text && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <span className="text-sm text-gray-400">
                      <span className="text-primary-400 font-medium">Pricing:</span> {service.pricing_text}
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {services.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No services available. Please check back later.</p>
          </div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="glass rounded-3xl p-8">
            <h2 className="text-2xl font-bold text-white mb-3">
              Ready to Start Your Project?
            </h2>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
              Let's discuss how I can help bring your ideas to life. I'm available for both short-term and long-term projects.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/hire" className="px-8 py-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300">
                Hire Me
              </a>
              <a href="/contact" className="px-8 py-3 border border-gray-700 rounded-lg font-medium text-gray-300 hover:bg-white/5 transition-all duration-300">
                Contact Me
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;